// Volání serveru. Token relace záložky žije v sessionStorage a posílá se v hlavičce X-Iwan-Token – nikdy v URL.
// (Authorization zůstává volná pro Basic Auth hostingu nebo proxy.)
import type { ApiErrorBody, CommandResult } from './contract';

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly fields: Record<string, string> | null = null,
    public readonly id = '',
    public readonly params: Record<string, unknown> | null = null,
    public readonly debug?: string,
  ) {
    super(message);
  }
}

const TOKEN_KEY = 'iwan.token';
let prefix = '';
let token: string | null = null;
let authLostHandler: (() => void) | null = null;
let lockedHandler: (() => void) | null = null;
let elevationHandler: (() => Promise<boolean>) | null = null;
let elevating: Promise<boolean> | null = null;

export function configureApi(p: string): void {
  prefix = p.replace(/\/$/, '');
  try {
    token = sessionStorage.getItem(TOKEN_KEY);
  } catch {
    token = null;
  }
}

export function setToken(t: string | null): void {
  token = t;
  try {
    if (t) sessionStorage.setItem(TOKEN_KEY, t);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* soukromý režim bez úložiště – relace vydrží jen do obnovení stránky */
  }
}

export function getToken(): string | null {
  return token;
}

export function onAuthLost(h: () => void): void {
  authLostHandler = h;
}

/** Server vyžaduje zvýšené ověření (428): handler ho vyžádá od uživatele; při úspěchu se požadavek zopakuje. */
export function onElevationRequired(h: () => Promise<boolean>): void {
  elevationHandler = h;
}

export function onLocked(h: () => void): void {
  lockedHandler = h;
}

export function url(path: string): string {
  return prefix + (path.startsWith('/') ? path : '/' + path);
}

export async function api<T>(path: string, body?: unknown, init: { method?: string; raw?: BodyInit; query?: Record<string, string>; noElevate?: boolean } = {}): Promise<T> {
  const method = init.method ?? (body === undefined && init.raw === undefined ? 'GET' : 'POST');
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers['X-Iwan-Token'] = token;
  let payload: BodyInit | undefined = init.raw;
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  let target = url(path);
  if (init.query) target += (target.includes('?') ? '&' : '?') + new URLSearchParams(init.query).toString();
  const res = await fetch(target, { method, headers, body: payload, credentials: 'same-origin' });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new ApiError('bad_response', 'core.err.internal', res.status);
    }
  }
  if (!res.ok) {
    const err = (data as ApiErrorBody | null)?.error;
    const e = new ApiError(err?.code ?? 'http_' + res.status, err?.message ?? 'core.err.internal', res.status, err?.fields ?? null, err?.id ?? '', err?.params ?? null, err?.debug);
    if (res.status === 401 && !path.startsWith('/auth/')) authLostHandler?.();
    if (res.status === 423) lockedHandler?.();
    if (res.status === 428 && elevationHandler && !init.noElevate) {
      // zvýšené ověření: jeden dialog i pro víc souběžných požadavků, po úspěchu zopakovat
      elevating ??= elevationHandler().finally(() => { elevating = null; });
      if (await elevating) return api<T>(path, body, { ...init, noElevate: true });
    }
    throw e;
  }
  return data as T;
}

/**
 * Stažení souboru (podepsaný odkaz) s průběhem: onProgress(staženo, celkem | null). Chyba HTTP = ApiError.
 */
export async function fetchBlob(url: string, onProgress?: (loaded: number, total: number | null) => void, signal?: AbortSignal): Promise<Blob> {
  const res = await fetch(url, { credentials: 'same-origin', signal });
  if (!res.ok) throw new ApiError('http', 'core.err.http', res.status);
  const total = Number(res.headers.get('Content-Length')) || null;
  const type = res.headers.get('Content-Type') ?? '';
  if (!res.body || !onProgress) return res.blob();
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.length;
    onProgress(loaded, total);
  }
  return new Blob(chunks as BlobPart[], { type });
}

/** Akce okna: POST /action/<app>/<okno>/<akce> */
export function action<T>(app: string, window: string, act: string, params: Record<string, unknown> = {}, payload: Record<string, unknown> = {}): Promise<T> {
  return api<T>(`/action/${app}/${window}/${act}`, { params, payload });
}

/** Rozhraní jádra: POST /core/<služba>/<metoda> */
export function core<T>(service: string, method: string, body: Record<string, unknown> = {}): Promise<T> {
  return api<T>(`/core/${service}/${method}`, body);
}

export function runCommand(app: string, window: string, params: Record<string, unknown>, command: string, selection: string[], data: Record<string, unknown> = {}): Promise<CommandResult> {
  return action<CommandResult>(app, window, 'cmd', params, { command, selection, data });
}

export async function upload(parentId: string, files: FileList | File[], onProgress?: (done: number, total: number) => void, temp = false, paths?: string[]): Promise<unknown> {
  const form = new FormData();
  const list = Array.from(files);
  list.forEach((f, i) => {
    form.append('files[]', f, f.name);
    form.append('paths[]', paths?.[i] ?? '');
  });
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url(temp ? '/upload?temp=1' : '/upload?parent=' + encodeURIComponent(parentId)));
    if (token) xhr.setRequestHeader('X-Iwan-Token', token);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      let data: unknown = null;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        /* ignore */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else {
        const err = (data as ApiErrorBody | null)?.error;
        if (xhr.status === 401) authLostHandler?.();
        reject(new ApiError(err?.code ?? 'http_' + xhr.status, err?.message ?? 'core.err.internal', xhr.status, err?.fields ?? null, err?.id ?? ''));
      }
    };
    xhr.onerror = () => reject(new ApiError('network', 'core.ui.offline', 0));
    xhr.send(form);
  });
}
